import React, { useEffect, useState, useRef } from "react";
import * as S from "../../style/StyleComponentSection";
import * as U from "../../style/StyleComponentUtility";
import { get_brand_select, get_product_select, get_integrated_construction_list } from '../../Axios';
import { Brand, Product, IntegratedConstruction } from "../../model/product";
import { useDispatch } from "react-redux";
import {
    reduxAddEstimateSelectedProduct,
    reduxChangeEstimateDivision,
    reduxChangeEstimateHighStatus,
    reduxChangeEstimateProductConsumptionStatus,
    reduxChangeEstimateConstructionReservationExpectedDate
} from "../../redux/EstimateSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/Store";
import {
    reduxAddReservationSelectedProduct,
    reduxChangeInstallSelectedProduct,
    reduxChangeConstructionReservationExpectedDate,
    reduxChangeProductConsumptionStatus,
    reduxChangeReservationDirectProductIndexList,
    reduxChangeReservationDivision,
    reduxChangeReservationHighStatus,
    reduxChangeReservationSelectedProduct,
    reduxChangeReservationWithRemoval
} from "../../redux/ReservationSlice";

import DaumPostCode from "../Utility/DaumPostCode";
import closeIcon from "../../assets/icons/close.svg";
import searchIcon from "../../assets/icons/search.svg";
import { search_regExp } from "../../Util";
import { varHighStatus, serviceDivision } from "../../Variable";
import FloatingCart from "./FloatingCart";

interface ProductPickerProps {
    brandList: Brand[];
    title?: string;
    onSelectProduct: (product: Product) => void;
}

const ProductPicker: React.FC<ProductPickerProps> = ({ brandList, title, onSelectProduct }) => {
    const [productList, setProductList] = useState<Product[]>([]);
    const [productRenderingList, setProductRenderingList] = useState<Product[]>([]);
    const [productTypeList, setProductTypeList] = useState<any[]>([]);
    const [activeBrand, setActiveBrand] = useState(0);
    const [activeType, setActiveType] = useState("");
    const [searchText, setSearchText] = useState("");
    const productSectionRef = useRef<HTMLDivElement | null>(null);

    // 제품 검색 로직
    useEffect(() => {
        let list = [];
        let search = searchText.toLowerCase().trim().split(' ').join('');
        // 타입 & 검색 필터
        if(!activeType && !searchText) {
            list = productList;
        } else if (!activeType && searchText) {
            list = productList.filter((tmp) => {
                return (
                    search_regExp(tmp.product_kor_name).includes(search) ||
                    search_regExp(tmp.color_name).includes(search) ||
                    search_regExp(tmp.product_eng_name).includes(search) ||
                    search_regExp(tmp.product_name).includes(search)
                );
            });
        } else if (activeType && !searchText) {
            list = productList.filter((tmp) =>
                tmp.product_type_kor_name === activeType
            );
        } else {
            list = productList.filter((tmp) => {
                return (
                    (
                        search_regExp(tmp.product_kor_name).includes(search) ||
                        search_regExp(tmp.color_name).includes(search) ||
                        search_regExp(tmp.product_eng_name).includes(search) ||
                        search_regExp(tmp.product_name).includes(search)
                    ) && tmp.product_type_kor_name === activeType
                );
            });
        };
        setProductRenderingList(list);
    }, [productList, searchText, activeType]);

    // 브랜드 선택시 해당 브랜드 제품리스트 가져오기
    const handleSelectBrand = (brandId:number) => {
        setActiveType("");
        setActiveBrand(brandId);
        get_product_select(brandId.toString()).then(result => {
            let list = [...result];
            for(let i = 0; i < result.length; i++) {
                list[i].brand_ID = brandId;
            };
            handleProductTypeList(list);
            setProductList(list);
            setProductRenderingList(list);
            requestAnimationFrame(() => scrollToProductSection());
        });
    };

    // 해당 브랜드 제품 타입 가져와서 탭 버튼 만들기
    const handleProductTypeList = (arr:any) => {
        let list:any[] = [];
        for(let i = 0; i < arr.length; i++) {
            const productType = arr[i].product_type_kor_name;
            if (!list.includes(productType)) {
                list.push(productType);
            }
        };
        setProductTypeList(list);
    };

    const scrollToProductSection = () => {
        if (!productSectionRef.current) return;
        const rect = productSectionRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetY = Math.max(rect.top + scrollTop - 100, 0);
        window.scrollTo({ top: targetY, behavior: "smooth" });
    };

    return (
        <>
            {title && <h4 className="text-left font-bold px-4">{title}</h4>}
            {title && <U.BreakLine $height={20} />}
            {/* 브랜드 선택 */}
            <S.SectionContent>
                <div className="section-sub-title-box">
                    <h4>브랜드 선택<span className="text-middleGray01 text-[16px] font-light"> (가나다 순)</span></h4>
                </div>
            </S.SectionContent>

            <U.BreakLine $height={40} />

            <div className="px-4">
                <S.BrandSelectBox>
                    {brandList.filter((tmp) => Number(tmp.brand_ID) !== 99).map((tmp) =>
                        <S.BrandItemBox
                        $btnSelect={activeBrand === tmp.brand_ID ? true : false}
                        onClick={() => handleSelectBrand(tmp.brand_ID)} key={tmp.brand_ID}>
                            <div>
                                <img width={50} height={50} src={tmp.brand_img} alt="" />
                                <p>{tmp.brand_kor_name}</p>
                            </div>
                        </S.BrandItemBox>
                    )}
                </S.BrandSelectBox>
            </div>

            <U.BreakLine $height={120}>
                <div className="horizontal-05-blk"></div>
            </U.BreakLine>

            {/* 제품 선택 */}
            <div ref={productSectionRef}>
                <S.SectionContent>
                    <div className="flex justify-between items-center w-[100%]">
                        <div className="section-sub-title-box">
                            <h4>제품 선택<span className="text-middleGray01 text-[16px] font-light"> (ABC 순)</span></h4>
                        </div>
                        <S.SearchBox>
                            <input
                                type="text" placeholder="시공 가능한 제품 검색하기" maxLength={16} value={searchText}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value.toLowerCase()) }
                                />
                            {searchText && <img className="close-icon" src={closeIcon} alt="" onClick={() => setSearchText("")} />}
                            <img className="search-icon" src={searchIcon} alt="" />
                        </S.SearchBox>
                    </div>
                </S.SectionContent>

                <div className="mx-4 mt-4 rounded-lg bg-gray-50 p-4 text-gray-600">
                    <p className="font-medium text-gray-800">찾으시는 제품이 없나요?</p>
                    <p className="mt-2 text-sm leading-relaxed">
                        제품명 또는 제품 사진을 준비해 앰비언스 담당자에게 문의해 주세요.
                        <br />
                        제품 확인 및 등록 후 시공 예약을 진행하실 수 있습니다.
                    </p>
                </div>

                {/* 제품 타입별로 리스트 보여주는 필터 */}
                <S.ProductTypeBox>
                    <div className="product-type-flex">
                        {productList.length > 0 &&
                            <S.ProductTypeButton onClick={() => { setActiveType(""); }} $btnSelect={activeType === "" ? true : false}>
                                <span>전체</span>
                            </S.ProductTypeButton>
                        }
                        {productTypeList.map((tmp, idx) =>
                            <S.ProductTypeButton onClick={() => { setActiveType(tmp); }} key={idx} $btnSelect={activeType === tmp ? true : false} >
                                <span>{tmp}</span>
                            </S.ProductTypeButton>
                        )}
                    </div>
                </S.ProductTypeBox>

                <S.ProductContainer>
                    {!productList.length ?
                        <div className="empty-box">브랜드를 선택해주세요.</div>
                        :
                        <S.ProductSelectBox>
                            {productRenderingList.map((tmp, idx) =>
                                <S.ProductItemBox onClick={() => onSelectProduct(tmp)} key={idx}>
                                    {tmp.product_kor_name} - {tmp.color_name}
                                </S.ProductItemBox>
                            )}
                        </S.ProductSelectBox>
                    }
                </S.ProductContainer>
            </div>
        </>
    );
};

interface Props {
    // step : 3 => 견적계산기, step : 5 => 서비스예약
    step               : number;
    directPhotoList    : File[];
    setDirectPhotoList : any;
    propsPriceList     : number[];
    installPriceList?  : number[];
    totalPrice         : number;
};

const SelectProductComp: React.FC<Props> = ({ step, directPhotoList, setDirectPhotoList, propsPriceList, installPriceList, totalPrice }) => {
    const dispatch = useDispatch();

    const [brandList, setBrandList] = useState<Brand[]>([]);
    
    const estimateData = useSelector((state: RootState) => state.estimateData);
    const ReservationData = useSelector((state: RootState) => state.reservationData);
    const withRemoval = step === 5 && ReservationData.reservation_division === 4 && ReservationData.reservation_with_removal === 1;
    
    const [activeDivision, setActiveDivision] = useState(0);
    const [activeHighStatus, setActiveHighStatus] = useState(0);

    const [warnEstimateDivision, setWarnEstimateDivision] = useState<boolean>(false);
    const [warnReservationDivision, setWarnReservationDivision] = useState<boolean>(false);
    
    const [isDirectProduct, setIsDirectProduct] = useState<boolean>(false);
    const [isFloatingCartOpen, setIsFloatingCartOpen] = useState<boolean>(true);
    const [directProductIndexList, setDirectProductIndexList] = useState<number[]>([]);
    const [directPreviewList, setDirectPreviewList] = useState<string[]>([]);
    // const [directPhotoList, setDirectPhotoList] = useState<File[]>([]);
    const [integratedConstructionList, setIntegratedConstructionList] = useState<IntegratedConstruction[]>([]);
    const [selectedConstructionIds, setSelectedConstructionIds] = useState<number[]>([]);
    
    // 이전 값 추적을 위한 ref
    const prevProductConsumptionStatusRef = useRef<number>(ReservationData.product_consumption_status);
    const prevReservationDivisionRef = useRef<number>(ReservationData.reservation_division);
    const lastScrollYRef = useRef<number>(0);

    // 렌더링 시 브랜드 리스트 가져오기
    useEffect(() => {
        get_brand_select().then(result => {
            setBrandList(result);
        });
    }, []);

    // redux에서 저장된 사항 화면에 반영하기
    useEffect(() => {
        if(step === 3) {
            setActiveDivision(estimateData.estimate_division);
            setActiveHighStatus(estimateData.estimate_high_status);
        } else if (step === 5) {
            setActiveDivision(ReservationData.reservation_division);
            setActiveHighStatus(ReservationData.reservation_high_status);
        }
    }, [ReservationData.reservation_division, ReservationData.reservation_high_status, estimateData.estimate_division, estimateData.estimate_high_status, step]);

    useEffect(() => {
        if(step === 5) {
            if(ReservationData.is_direct_product) {
                setIsDirectProduct(true);
            } else {
                setIsDirectProduct(false);
            }
        }
    }, [ReservationData.is_direct_product, step]);

    // 출고요청 가능 목록 가져오기
    useEffect(() => {
        if((ReservationData.reservation_division === 1 || withRemoval) && ReservationData.product_consumption_status === 2) {
            get_integrated_construction_list().then(result => {
                if(result.success && result.data) {
                    setIntegratedConstructionList(result.data);
                } else if(result.success === false) {
                    setIntegratedConstructionList([]);
                }
            }).catch(err => {
                console.error('출고요청 가능 목록 조회 실패:', err);
                setIntegratedConstructionList([]);
            });
        } else {
            setIntegratedConstructionList([]);
        }
    }, [ReservationData.product_consumption_status, ReservationData.reservation_division, ReservationData.reservation_with_removal, step, withRemoval]);

    useEffect(() => {
        if(step === 5 && prevReservationDivisionRef.current !== ReservationData.reservation_division) {
            const prevStatus = prevProductConsumptionStatusRef.current;
            
            // 탭 이동 시 판매건 연결 카트를 비운다
            if(prevReservationDivisionRef.current === 1 && prevStatus === 2) {
                dispatch(reduxChangeReservationSelectedProduct([]));
                setSelectedConstructionIds([]);
            }

            dispatch(reduxChangeProductConsumptionStatus(0));
            if(prevReservationDivisionRef.current === 4) {
                dispatch(reduxChangeInstallSelectedProduct([]));
                setSelectedConstructionIds([]);
            }
            prevReservationDivisionRef.current = ReservationData.reservation_division;
        }
    }, [ReservationData.reservation_division, step, dispatch]);

    useEffect(() => {
        if(step === 5 && (ReservationData.reservation_division === 1 || withRemoval)) {
            const prevStatus = prevProductConsumptionStatusRef.current;
            const currentStatus = ReservationData.product_consumption_status;
            
            // 시공에서 출고요청(2)로 변경하거나 출고요청(2)에서 다른 값으로 변경할 때 장바구니 초기화
            if((prevStatus !== 2 && currentStatus === 2) || (prevStatus === 2 && currentStatus !== 2)) {
                dispatch(withRemoval ? reduxChangeInstallSelectedProduct([]) : reduxChangeReservationSelectedProduct([]));
                setSelectedConstructionIds([]);
            }
            
            prevProductConsumptionStatusRef.current = currentStatus;
        }
    }, [ReservationData.product_consumption_status, ReservationData.reservation_division, ReservationData.reservation_with_removal, step, withRemoval, dispatch]);

    useEffect(() => {
        if(ReservationData.product_consumption_status === 2 && integratedConstructionList.length > 0) {
            const selectedProducts = withRemoval ? ReservationData.install_selected_product : ReservationData.selected_product;
            const cartInventoryIds = selectedProducts.map(product => product.construction_ID);
            const selectedIds = integratedConstructionList
                .filter(item => cartInventoryIds.includes(item.construction_ID))
                .map(item => item.construction_ID);
            setSelectedConstructionIds(selectedIds);
        }
    }, [ReservationData.selected_product, ReservationData.install_selected_product, ReservationData.product_consumption_status, ReservationData.reservation_with_removal, withRemoval, integratedConstructionList]);

    // 서비스 유형 선택
    const handleDivision = (e: React.MouseEvent<HTMLDivElement>) => {
        if(e.target === e.currentTarget ) return;
    
        const target = e.target as HTMLElement;
        const value = Number(target.getAttribute('data-value'));

        if(step === 3) {
            if(estimateData.estimate_division !== 0 && estimateData.selected_product.length >= 2) {
                setWarnEstimateDivision(true);
            }
            dispatch(reduxChangeEstimateDivision(value));
        }
        else if (step === 5) {
            if(ReservationData.reservation_division !== 0 && ReservationData.selected_product.length >= 2) {
                setWarnReservationDivision(true);
            }
            dispatch(reduxChangeReservationDivision(value));
        }
    };

    // 천고 선택
    const handleHighStatus = (e: React.MouseEvent<HTMLDivElement>) => {
        if(e.target === e.currentTarget ) return;
        
        const target = e.target as HTMLElement;
        const value = Number(target.getAttribute('data-value'));

        if(step === 3) {
            dispatch(reduxChangeEstimateHighStatus(value));
        }
        else if (step === 5) {
            dispatch(reduxChangeReservationHighStatus(value));
        }
    };

    // 선택한 제품 카트 리스트에 담기
    const handleSelectedProduct = (product_ID:number, product_kor_name:string, color_name:string, brand_ID:number, brand_kor_name: string, cartTarget: 'removal' | 'install' = 'removal') => {
        let list = [];
        if(step === 3) {
            for (const tmp of estimateData.selected_product) {
                list.push(tmp.product_ID);
            };
            if(list.includes(product_ID)) return alert('이미 추가한 제품입니다.');
        } else if (step === 5 && withRemoval && cartTarget === 'install') {
            for (const tmp of ReservationData.install_selected_product) {
                list.push(tmp.product_ID);
            };
            if(list.includes(product_ID)) return alert('이미 추가한 제품입니다.');
        } else if (step === 5) {
            for (const tmp of ReservationData.selected_product) {
                list.push(tmp.product_ID);
            };
            if(list.includes(product_ID)) return alert('이미 추가한 제품입니다.');
        }
        const obj = {
            product_ID   : product_ID,
            product_name : `${product_kor_name} - ${color_name}`,
            quantity     : 1,
            brand_ID     : brand_ID,
            brand_kor_name : brand_kor_name
        };
        if(step === 3) { dispatch(reduxAddEstimateSelectedProduct(obj));}
        else if (step === 5 && withRemoval) {
            if (cartTarget === 'install') {
                dispatch(reduxChangeInstallSelectedProduct([obj]));
            } else {
                dispatch(reduxChangeReservationSelectedProduct([obj]));
            }
        }
        else if (step === 5) { dispatch(reduxAddReservationSelectedProduct(obj)); }
    };

    // 출고요청 목록에서 선택한 항목을 장바구니에 추가
    const handleSelectConstruction = (item: IntegratedConstruction) => {
        const isSelected = selectedConstructionIds.includes(item.construction_ID);

        if (withRemoval) {
            if (isSelected) {
                dispatch(reduxChangeInstallSelectedProduct([]));
                setSelectedConstructionIds([]);
            } else {
                const brand = brandList.find(b => b.brand_kor_name === item.brand_kor_name);
                const brandId = brand ? brand.brand_ID : 0;
                const obj = {
                    product_ID   : item.color_ID,
                    construction_ID : item.construction_ID,
                    product_name : `${item.brand_kor_name} - ${item.product_name} / ${item.color_name}`,
                    quantity     : 1,
                    brand_ID     : brandId,
                    brand_kor_name : item.brand_kor_name
                };
                dispatch(reduxChangeInstallSelectedProduct([obj]));
                setSelectedConstructionIds([item.construction_ID]);
            }
            return;
        }

        if (isSelected) {
            // 선택 해제
            setSelectedConstructionIds(prev => 
                prev.filter(id => id !== item.construction_ID)
            );
            // 장바구니에서도 제거
            const updatedProducts = ReservationData.selected_product.filter(
                product => product.construction_ID !== item.construction_ID
            );
            dispatch(reduxChangeReservationSelectedProduct(updatedProducts));
        } else {
            // 이미 장바구니에 추가되어 있는지 확인
            const isAlreadyInCart = ReservationData.selected_product.some(
                product => product.construction_ID === item.construction_ID
            );
            
            if (isAlreadyInCart) {
                alert('이미 장바구니에 추가된 제품입니다.');
                return;
            }

            // 브랜드 ID 찾기
            const brand = brandList.find(b => b.brand_kor_name === item.brand_kor_name);
            const brand_ID = brand ? brand.brand_ID : 0;

            // 장바구니에 추가
            const obj = {
                product_ID   : item.color_ID,
                construction_ID : item.construction_ID,
                product_name : `${item.brand_kor_name} - ${item.product_name} / ${item.color_name}`,
                quantity     : 1,
                brand_ID     : brand_ID,
                brand_kor_name : item.brand_kor_name
            };
            
            dispatch(reduxAddReservationSelectedProduct(obj));
            setSelectedConstructionIds(prev => 
                [...prev, item.construction_ID]
            );
        }
    };

    useEffect(() => {
        if(step === 5) {
            let indexList = [...directProductIndexList];
            dispatch(reduxChangeReservationDirectProductIndexList(indexList));
        }
    }, [directProductIndexList, dispatch, step]);

    // 페이지 렌더링 시 리덕스에서 직접입력 list 다시 가져오기
    useEffect(() => {
        if(step === 5) {
            let indexList = ReservationData.direct_index_list;
            setDirectProductIndexList(indexList);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const insertDirectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        if(file) {
            let list = [...directPhotoList];
            list.push(file);
            setDirectPhotoList(list);
        };
    };

    useEffect(() => {
        let list = [];
        for (let i = 0; i < directPhotoList.length; i++) {
            const objectUrl = URL.createObjectURL(directPhotoList[i]);
            list.push(objectUrl);
        }
        setDirectPreviewList(list);
    }, [directPhotoList]);

    useEffect(() => {
        const handleScroll = () => {
            if (!isFloatingCartOpen) return;
            
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const isScrollingDown = scrollTop > lastScrollYRef.current;
            lastScrollYRef.current = scrollTop;
            
            const isAtBottom = scrollTop + windowHeight >= documentHeight;
            
            if (isAtBottom && isScrollingDown) {
                setIsFloatingCartOpen(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isFloatingCartOpen]);

    const removeDirectFile = (idx: number) => {
        let list = [...directPhotoList];
        list.splice(idx, 1);
        setDirectPhotoList(list);
    };

    const consumptionStatusBlock = (
        <div className="w-full">
            <div className="flex justify-center mt-5 mx-5">
                <S.EmptyReleaseButton
                    onClick={() => {
                        if (step === 3) {
                            dispatch(reduxChangeEstimateProductConsumptionStatus(0))
                        } else if (step === 5) {
                            dispatch(reduxChangeProductConsumptionStatus(0))
                        }
                    }}
                    $btnSelect={step === 3 ? estimateData.product_consumption_status === 0 : ReservationData.product_consumption_status === 0}
                >
                    제품 소지
                </S.EmptyReleaseButton>
                <S.EmptyReleaseButton
                    onClick={() => {
                        if (step === 3) {
                            dispatch(reduxChangeEstimateProductConsumptionStatus(1))
                        } else if (step === 5) {
                            dispatch(reduxChangeProductConsumptionStatus(1))
                        }
                    }}
                    $btnSelect={step === 3 ? estimateData.product_consumption_status === 1 : ReservationData.product_consumption_status === 1}
                >
                    제품 미소지
                </S.EmptyReleaseButton>
                <S.EmptyReleaseButton
                    onClick={() => {
                        if (step === 3) {
                            dispatch(reduxChangeEstimateProductConsumptionStatus(2))
                        } else if (step === 5) {
                            dispatch(reduxChangeProductConsumptionStatus(2))
                        }
                    }}
                    $btnSelect={step === 3 ? estimateData.product_consumption_status === 2 : ReservationData.product_consumption_status === 2}
                >
                    출고요청
                </S.EmptyReleaseButton>
            </div>
            <br />
            <div className="w-full flex justify-center mt-2 mx-5">
                <div className="bg-gray-100 px-4 py-3 rounded-md w-full flex">
                    <span className="text-[15px] font-bold leading-[1.5]">
                        {(step === 3 ? estimateData.product_consumption_status : ReservationData.product_consumption_status) === 0 && '제품 소지:'}
                        {(step === 3 ? estimateData.product_consumption_status : ReservationData.product_consumption_status) === 1 && '제품 미소지:'}
                        {(step === 3 ? estimateData.product_consumption_status : ReservationData.product_consumption_status) === 2 && '출고요청:'}
                        &nbsp;
                    </span>
                    <p className="text-[15px] text-start text-gray-700 leading-[1.5]">
                        {(step === 3 ? estimateData.product_consumption_status : ReservationData.product_consumption_status) === 0 && '고객이 제품을 이미 보유한 상태입니다. 시공만 진행합니다.'}
                        {(step === 3 ? estimateData.product_consumption_status : ReservationData.product_consumption_status) === 1 && (
                            <p className="text-[15px] text-start text-gray-700">
                                고객이 아직 제품을 보유하고 있지 않은 상태입니다. <br />
                                고객에게 제품이 도착할 예정일을 입력해 주세요.
                            </p>
                        )}
                        {(step === 3 ? estimateData.product_consumption_status : ReservationData.product_consumption_status) === 2 && (
                            <p className="text-[15px] text-start text-gray-700">
                                앰비언스 본사에서 제품 출고 후 시공까지 진행합니다. <br />
                                아래 조건을 모두 충족하는 판매 건만 목록에 표시됩니다. <br />
                                - 입금완료 처리된 건 <br />
                                - 앰비언스 창고에 재고가 배정된 건 (재고 상태: 창고/출고가능) <br />
                                - 아직 시공 예약(출고요청)이 진행되지 않은 건
                            </p>
                        )}
                    </p>
                </div>
            </div>
            {(step === 3 ? estimateData.product_consumption_status : ReservationData.product_consumption_status) === 1 &&
                <div className="w-full flex flex-col gap-3 justify-start px-4 py-3 mx-5">
                    <div className="text-md font-bold">
                        <span>제품 도착 예정일</span>
                    </div>
                    <input
                        type="text"
                        value={step === 3 ? estimateData.construction_reservation_expected_date : ReservationData.construction_reservation_expected_date}
                        onChange={(e) => {
                            if (step === 3) {
                                dispatch(reduxChangeEstimateConstructionReservationExpectedDate(e.target.value))
                            } else if (step === 5) {
                                dispatch(reduxChangeConstructionReservationExpectedDate(e.target.value))
                            }
                        }}
                        placeholder="예: 2025-12-15"
                        className="w-[500px] h-[40px] border border-gray-300 rounded-[5px] p-2"
                    />
                </div>
            }
            </div>
    );

    const releaseListBlock = (
        <>
            <S.SectionContent>
                <div className="section-sub-title-box">
                    <h4>출고요청 가능 목록</h4>
                </div>
            </S.SectionContent>
            <U.BreakLine $height={20} />
            <S.InputBoxContainer>
                {integratedConstructionList.length > 0 ? (
                    <div className="w-full overflow-x-auto rounded-lg shadow-sm">
                        <table className="w-full border-collapse bg-white">
                            <thead>
                                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 text-xs text-left font-semibold text-gray-700">
                                    <th className="border-b border-gray-200 px-4 py-3">계약일</th>
                                    <th className="border-b border-gray-200 px-4 py-3">브랜드</th>
                                    <th className="border-b border-gray-200 px-4 py-3">제품유형</th>
                                    <th className="border-b border-gray-200 px-4 py-3">제품명</th>
                                    <th className="border-b border-gray-200 px-4 py-3">색상</th>
                                    <th className="border-b border-gray-200 px-4 py-3">재고 상태</th>
                                    <th className="border-b border-gray-200 px-4 py-3 text-center">선택</th>
                                </tr>
                            </thead>
                            <tbody>
                                {integratedConstructionList.map((item, idx) => {
                                    const isSelected = selectedConstructionIds.includes(item.construction_ID);
                                    return (
                                        <tr
                                            key={item.construction_ID || idx}
                                            className={`transition-colors duration-150 ${
                                                isSelected
                                                    ? 'bg-blue-50 hover:bg-blue-100'
                                                    : 'hover:bg-gray-50'
                                            } text-left`}
                                        >
                                            <td className="border-b border-gray-200 px-3 py-3 text-sm text-gray-800">{item.contract_date || '-'}</td>
                                            <td className="border-b border-gray-200 px-3 py-3 text-sm text-gray-800">{item.brand_kor_name || '-'}</td>
                                            <td className="border-b border-gray-200 px-3 py-3 text-sm text-gray-800">{item.product_type_kor_name || '-'}</td>
                                            <td className="border-b border-gray-200 px-3 py-3 text-sm text-gray-800 max-w-xs truncate" title={item.product_name || ''}>{item.product_name || '-'}</td>
                                            <td className="border-b border-gray-200 px-3 py-3 text-sm text-gray-800">{item.color_name || '-'}</td>
                                            <td className="border-b border-gray-200 px-3 py-3 text-sm">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                                }`}>
                                                    {item.inventory_status || '-'}
                                                </span>
                                            </td>
                                            <td className="border-b border-gray-200 px-4 py-3 text-center">
                                                <button
                                                    onClick={() => handleSelectConstruction(item)}
                                                    className={`px-4 py-2 rounded-md text-xs font-medium transition-all duration-200 ${
                                                        isSelected
                                                            ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                    }`}
                                                >
                                                    {isSelected ? '선택됨' : '선택'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12 px-4 text-gray-500 bg-gray-50 rounded-lg">
                        <p className="text-lg">출고 요청 가능한 목록이 없습니다.</p>
                        <p className="text-sm mt-3 leading-[1.6]">
                            미입금 상태이거나 재고가 아직 배정되지 않은 판매 건은 표시되지 않습니다. <br />
                            입금 확인 및 재고 배정이 완료되면 자동으로 표시되며, <br />
                            확인이 필요한 경우 앰비언스 담당자에게 문의해 주세요.
                        </p>
                    </div>
                )}
            </S.InputBoxContainer>
        </>
    );

    return (
        <>
            <FloatingCart 
                step={step}
                propsPriceList={propsPriceList}
                installPriceList={installPriceList}
                setDirectProductIndexList={setDirectProductIndexList}
                totalPrice={totalPrice}
                isOpen={isFloatingCartOpen}
                setIsOpen={setIsFloatingCartOpen}
            />
            <S.SectionContainer>
                <S.SectionWidth $width={900}>

                <U.BreakLine $height={60} />

                <S.SectionContent>
                    <div className="section-sub-title-box">
                        <h4>신청할 서비스 선택</h4>
                    </div>
                </S.SectionContent>

                <U.BreakLine $height={30} />
                <S.SectionContent>
                    <div onClick={handleDivision} className="section-flex-box w-[100%] justify-center">
                        {serviceDivision.map(item => (
                            <S.EmptyRoundedButton
                                key={item.value}
                                data-value={Number(item.value)}
                                $btnSelect={activeDivision === Number(item.value) ? true : false}
                                $width={280}
                            >
                                {item.label}
                            </S.EmptyRoundedButton>
                        ))}
                    </div>
                </S.SectionContent>

                {((step === 3 && estimateData.estimate_division === 1) || (step === 5 && ReservationData.reservation_division === 1)) &&
                    consumptionStatusBlock
                }

                {((step === 3 && estimateData.estimate_division === 4) || (step === 5 && ReservationData.reservation_division === 4)) &&
                <div className="w-full">
                    <div className="w-full flex justify-center mt-5 mx-5">
                        <div className="bg-gray-100 px-4 py-3 rounded-md w-full flex">
                            <span className="text-[15px] font-bold leading-[1.5]">
                                탈거:&nbsp;
                            </span>
                            <p className="text-[15px] text-start text-gray-700 leading-[1.5]">
                                탈거된 제품은 별도 보관 및 운송이 제공되지 않으므로, 고객님께서 현장에서 직접 가지고 가셔야 합니다.
                            </p>
                        </div>
                    </div>
                </div>
                }

                {((step === 3 && estimateData.estimate_division === 2) || (step === 5 && ReservationData.reservation_division === 2)) &&
                <div className="w-full">
                    <div className="w-full flex justify-center mt-5 mx-5">
                        <div className="bg-gray-100 px-4 py-3 rounded-md w-full flex">
                            <span className="text-[15px] font-bold leading-[1.5]">
                                A/S:&nbsp;
                            </span>
                            <p className="text-[15px] text-start text-gray-700 leading-[1.5]">
                                전구 교체 · 줄 길이 조절 등에 한하여 신청 가능합니다.
                            </p>
                        </div>
                    </div>
                </div>
                }

                {/* 탈거+시공 동시 진행 토글 (탈거탭 한정). ON 시 탈거 1건 + 시공 1건 별도 생성 */}
                {step === 5 && ReservationData.reservation_division === 4 &&
                    <S.SectionContent>
                        <div className="w-full flex flex-col items-start gap-2 px-5 mt-[40px]">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={ReservationData.reservation_with_removal === 1}
                                    onChange={(e) => {
                                        dispatch(reduxChangeReservationWithRemoval(e.target.checked ? 1 : 0));
                                        if (!e.target.checked) {
                                            dispatch(reduxChangeInstallSelectedProduct([]));
                                            dispatch(reduxChangeProductConsumptionStatus(0));
                                            setSelectedConstructionIds([]);
                                        }
                                    }}
                                />
                                <span className="text-md font-bold">시공도 함께 진행합니다 (탈거 비용 50%)</span>
                            </label>
                            <p className="text-sm text-gray-600">
                                * 기존 제품 탈거와 신규 제품 시공을 함께 신청합니다. 탈거지·시공지 주소와 희망일을 각각 입력해 주세요. 탈거할 제품과 시공할 제품은 각각 1개씩 담을 수 있습니다.
                            </p>
                        </div>
                    </S.SectionContent>
                }

                <U.BreakLine $height={20} />

                {warnEstimateDivision && step === 3 &&
                    <div className="text-pointOrange pl-5 text-sm">
                        * 같은 서비스만 견적 가능합니다. 서비스 변경 시 이전에 선택한 서비스는 변경 됩니다.
                    </div>
                }
                {warnReservationDivision && step === 5 &&
                    <div className="text-pointOrange pl-5 text-sm">
                        * 같은 서비스만 예약 가능합니다. 서비스 변경 시 같은 서비스별로 예약이 됩니다.
                    </div>
                }

                {/* <U.BreakLine $height={10} /> */}
                <U.BreakLine $height={60}>
                    <div className="horizontal-05-blk"></div>
                </U.BreakLine>

                {/* 이동 시공시 이전주소 와 새주소 */}
                {step === 3 && activeDivision === 3 &&
                    <>
                    <S.InputBoxContainer>
                        <div className="address-section">
                            <span>고객 주소</span>
                            <DaumPostCode addressSelect={1} />
                        </div>
                        <div className="vertical-line"></div>
                        <div className="address-section">
                            <span>이전할 주소</span>
                            <DaumPostCode addressSelect={2} />
                        </div>
                    </S.InputBoxContainer>
                    <U.BreakLine $height={60}>
                        <div className="horizontal-05-blk"></div>
                    </U.BreakLine>
                    </>
                }

                <U.BreakLine $height={30} />

                {ReservationData.reservation_division === 1 && ReservationData.product_consumption_status === 2 ? 
                    releaseListBlock
                :
                <>
                {withRemoval ?
                    <>
                        <ProductPicker
                            brandList={brandList}
                            title="탈거할 제품 선택"
                            onSelectProduct={(p) => handleSelectedProduct(p.color_ID, p.product_kor_name, p.color_name, p.brand_ID, p.brand_kor_name, 'removal')}
                        />
                        <U.BreakLine $height={60}>
                            <div className="horizontal-05-blk"></div>
                        </U.BreakLine>
                        <h4 className="text-left font-bold px-4">시공할 제품 선택</h4>
                        <U.BreakLine $height={20} />
                        {consumptionStatusBlock}
                        <U.BreakLine $height={20} />
                        {ReservationData.product_consumption_status === 2 ? releaseListBlock :
                            <ProductPicker
                                brandList={brandList}
                                onSelectProduct={(p) => handleSelectedProduct(p.color_ID, p.product_kor_name, p.color_name, p.brand_ID, p.brand_kor_name, 'install')}
                            />
                        }
                    </>
                    :
                    <ProductPicker brandList={brandList} onSelectProduct={(p) => handleSelectedProduct(p.color_ID, p.product_kor_name, p.color_name, p.brand_ID, p.brand_kor_name)} />
                }

                {isDirectProduct &&
                    <S.InputItemBox $inputWidth={0}>
                        <br />
                        {/* TODO: (코멘트 & UI 수정 필요) */}
                        <div className="input-title-box">
                            <span>직접입력 하시는 제품은 반드시 사진이 필요합니다.</span>
                        </div>
                        <S.PhotoBox $gridColumn={8}>         
                        {directProductIndexList.map((tmp, idx) => 
                            <div key={idx}>
                                {directPhotoList.length >= (idx+1) ?
                                    <div className="prev-item" onClick={() => removeDirectFile(idx)}>
                                        <img className="prev-img" src={directPreviewList[idx]} alt="" />
                                        <div className="prev-hover">삭제</div>
                                    </div>
                                    :
                                    <div>
                                        <input type="file" id={`directPhoto${idx+1}`} name="direct_photo_list" accept=".png, .jpg, .jpeg, .webp, .gif .heif" onChange={(e) => insertDirectFile(e)} />
                                        <label htmlFor={`directPhoto${idx+1}`}>
                                            <div className="photo-item">+</div>
                                        </label>
                                    </div>
                                }
                                <p className="text-sm">직접입력_{idx+1}</p>
                            </div>
                        )}
                        </S.PhotoBox>
                    </S.InputItemBox>
                }
                </>
                }
                <U.BreakLine $height={60} />

                <U.BreakLine $height={30} style={{alignItems : "start"}}>
                    <div className="horizontal-05-blk"></div>
                </U.BreakLine>

                <U.BreakLine $height={30} />
                
                <S.SectionContent>
                    <div className="section-sub-title-box">
                        <h4>천장 높이 (천고) 선택</h4>
                    </div>
                </S.SectionContent>
                <U.BreakLine $height={30} />
                <S.SectionContent>
                    <div className="flex flex-col items-center w-[100%] justify-center">
                        <div onClick={handleHighStatus} className="section-flex-box w-[100%] justify-center">
                            {varHighStatus.map((tmp, idx) => (
                                <S.EmptyRoundedButton data-value={tmp.value} $btnSelect={activeHighStatus === Number(tmp.value) ? true : false} key={idx} $width={280}>{tmp.label}</S.EmptyRoundedButton>
                            ))}
                        </div>
                        {activeHighStatus === 3 && (
                            <h5 className="text-black-500 text-md mt-7">
                                천장 높이가 6m 초과인 경우,{'\n'}<span className="text-red-500">아래의 서비스 이용 정보 입력에서 '요청사항'란에 천장 높이를 꼭 기록해주세요.</span>
                            </h5>
                        )}
                    </div>
                </S.SectionContent>
                <U.BreakLine $height={60} />

                <U.BreakLine $height={30} style={{alignItems : "start"}}>
                    <div className="horizontal-05-blk"></div>
                </U.BreakLine>

                </S.SectionWidth>
            </S.SectionContainer>
        </>
    )
};

export default SelectProductComp;
